package com.kanati.shared.admin

import com.kanati.shared.district.District
import com.kanati.shared.school.School
import kotlinx.serialization.Serializable

const val DEFAULT_OPERATOR_ROLE = "OPERATOR"
const val DEFAULT_ADMIN_TOKEN_TYPE = "Bearer"

@Serializable
enum class OperatorRole {
    OPERATOR,
    SUPERADMIN,
}

@Serializable
data class Operator(
    val id: String,
    val email: String,
    val displayName: String,
    val active: Boolean,
    val role: OperatorRole = OperatorRole.OPERATOR,
)

@Serializable
data class DistrictAccess(
    val district: District,
    val adminAccess: Boolean,
    val posAccess: Boolean,
    val districtWide: Boolean,
    val schools: List<School> = emptyList(),
)

@Serializable
data class SchoolAccess(
    val district: District,
    val school: School,
    val adminAccess: Boolean,
    val posAccess: Boolean,
)

@Serializable
enum class AccessScope {
    DISTRICT,
    SCHOOL,
}

@Serializable
data class OperatorAccessAssignment(
    val scope: AccessScope,
    val adminAccess: Boolean,
    val posAccess: Boolean,
    val districtWide: Boolean = false,
    val schools: List<School> = emptyList(),
)

@Serializable
data class OperatorAccessAssignmentRequest(
    val scope: AccessScope,
    val adminAccess: Boolean,
    val posAccess: Boolean,
    val districtWide: Boolean = false,
    val schoolIds: List<String> = emptyList(),
)

@Serializable
data class ManagedOperatorSummary(
    val operator: Operator,
    val district: District,
    val assignment: OperatorAccessAssignment,
)

@Serializable
data class CreateOperatorRequest(
    val districtId: String,
    val email: String,
    val displayName: String,
    val password: String,
    val active: Boolean = true,
    val assignment: OperatorAccessAssignmentRequest,
)

@Serializable
data class UpdateOperatorRequest(
    val districtId: String,
    val displayName: String? = null,
    val password: String? = null,
    val active: Boolean? = null,
    val assignment: OperatorAccessAssignmentRequest? = null,
)

@Serializable
enum class OperableSchoolSource {
    DISTRICT_WIDE,
    DISTRICT_RESTRICTED,
    SCHOOL,
}

@Serializable
data class OperableSchoolAccess(
    val district: District,
    val school: School,
    val source: OperableSchoolSource,
    val adminAccess: Boolean,
    val posAccess: Boolean,
)

@Serializable
data class CurrentOperatorContextResponse(
    val operator: Operator,
    val districtAccess: List<DistrictAccess> = emptyList(),
    val schoolAccess: List<SchoolAccess> = emptyList(),
    val operableSchools: List<OperableSchoolAccess> = emptyList(),
)

@Serializable
data class LoginRequest(
    val email: String,
    val password: String,
)

@Serializable
data class LoginResponse(
    val accessToken: String,
    val tokenType: String = DEFAULT_ADMIN_TOKEN_TYPE,
)

@Serializable
data class TrustedOperatorRegistrationRequest(
    val email: String,
    val displayName: String,
    val password: String,
    val trustedSourceToken: String,
)

@Serializable
data class TrustedOperatorRegistrationResponse(
    val accessToken: String,
    val tokenType: String = DEFAULT_ADMIN_TOKEN_TYPE,
    val context: CurrentOperatorContextResponse,
)
